import styled from '@emotion/styled';
import {Container} from '@nfq/react-grid';
import {CookieConsentProvider} from '@use-cookie-consent/react';
import {AnimatePresence} from 'motion/react';
import * as motion from 'motion/react-m';

import {CookieConsent} from 'UI/components/action/CookieConsent';

import {PagesTransition} from 'UI/animations/layout';
import {useRouterCrossPageScroll} from 'UI/hooks/useRouterCrossPageScroll';
import {Footer} from 'UI/modules/LachenschmidLayout/Footer';
import {Header} from 'UI/modules/LachenschmidLayout/Header';
import {MetaData} from 'UI/modules/LachenschmidLayout/MetaData';
import {Tracking} from 'UI/modules/LachenschmidLayout/Tracking';

import type {PageData} from 'PayloadUI/dataSelectors/getLachenschmidPageData';
import type {WithChildren} from 'types/global';
import type {Footer as FooterType, Header as HeaderType} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the shape of the props expected by the `LachenschmidLayout` component.
 * It specifies the required layout data such as header and footer content, as well as routing and metadata for page transitions.
 * Optional properties like `testId` and `title` allow for enhanced testing and browser tab labeling.
 * This interface ensures that the layout is flexible, testable, and semantically complete.
 */
interface ComponentProps {
    /**
     * The data required to render the footer component, typically structured content like links or contact info.
     */
    footerData: FooterType;
    /**
     * The data required to render the header component, such as navigation or branding elements.
     */
    headerData: HeaderType;
    /**
     * Combined metadata used for SEO, structured data, and canonical URL generation.
     */
    pageMetaData: PageData['pageMetaData'];
    /**
     * The route used to differentiate pages for animation transitions. Passed as the React `key` to re-trigger animations.
     */
    route: string;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'LachenschmidLayout'
     */
    testId?: string;
}

/**
 * Renders the main layout structure for the Lachenschmid website, including metadata, analytics, header, animated content, and footer.
 * This layout component serves as the root wrapper for all pages and ensures consistent structure and behavior across the site.
 * It includes the `<MetaData />` component for rendering SEO and structured data, a Google Analytics tracking script,
 * a header with branding and navigation, a content section with page transitions using `AnimatePresence`, and a global footer.
 * The `route` prop is used as a React `key` to re-trigger animation transitions when the page changes.
 * The `testId` prop is used to append stable identifiers for end-to-end and component tests on header and footer sections.
 *
 * @param props              The component props.
 * @param props.children     The main page content to be rendered inside the layout.
 * @param props.footerData   The footer data structure containing content such as links and contact info.
 * @param props.headerData   The header data structure used to render branding and navigation.
 * @param props.pageMetaData The page metadata used for SEO, Open Graph tags, breadcrumbs, and canonical links.
 * @param props.route        A unique identifier used to trigger animations when route changes.
 * @param props.testId       Optional string used to assign `data-cy` test IDs to layout sections.
 * @returns The complete layout including meta, analytics, header, animated content area, and footer.
 *
 * @example
 * ```tsx
 * <LachenschmidLayout
 *   footerData={footer}
 *   headerData={header}
 *   pageMetaData={meta}
 *   route="/leistungen/formteile"
 * >
 *   <PageContent />
 * </LachenschmidLayout>
 * ```
 */
const LachenschmidLayout = ({
    children,
    footerData,
    headerData,
    pageMetaData,
    route,
    testId = 'LachenschmidLayout'
}: WithChildren<ComponentProps>) => {
    useRouterCrossPageScroll();

    return (
        <CookieConsentProvider>
            <CookieConsent>
                <MetaData pageMetaData={pageMetaData} />
                <Tracking />
                <Header headerData={headerData} testId={`${testId}-Header`} />
                <AnimatePresence mode="wait">
                    <Content
                        key={route}
                        animate="enter"
                        as="main"
                        exit="exit"
                        initial="initial"
                        variants={PagesTransition}
                        hasNoPadding
                        isFluid
                    >
                        {children}
                    </Content>
                </AnimatePresence>
                <Footer footerData={footerData} testId={`${testId}-Footer`} />
            </CookieConsent>
        </CookieConsentProvider>
    );
};

LachenschmidLayout.displayName = 'LachenschmidLayout';

export {LachenschmidLayout};

const Content = styled(motion.create(Container), {shouldForwardProp: prop => !prop.startsWith('$')})`
    flex: 1 2 auto;
`;